import type {
	PartnerKeyStatisticResponseDto,
	PartnerKeyStatisticsFilterDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { defaultTableConfig } from "Infrastructure/Utilities/defaultTableConfig";
import { usePartnerKeyStatisticsQuery } from "Partner/Modules/Statistics/Api/Query/usePartnerKeyStatisticsQuery";
import { calcCancelledOrdersPercentage } from "Partner/Modules/Statistics/utils/calculations";
import { PartnerPaths } from "Partner/Routes/Routing";
import { Typography } from "@mui/material";
import { createMRTColumnHelper } from "material-react-table";
import { useMemo } from "react";
import { Link } from "react-router";

type TableAction = {
	label: string;
	action: () => void;
};

type PartnerStatisticsTableRow = PartnerKeyStatisticResponseDto & {
	actions: TableAction[];
};

const partnerStatisticsResource = Resources.base.partner.statistics;

export const usePartnerKeyStatisticsTable = (
	filter: PartnerKeyStatisticsFilterDto,
) => {
	const { t } = useResource();
	const columnHelper = createMRTColumnHelper<PartnerStatisticsTableRow>();

	const { data: statistics, isLoading } = usePartnerKeyStatisticsQuery(filter);

	const tableData: PartnerStatisticsTableRow[] = useMemo(
		() =>
			statistics
				?.filter((statistic) => statistic.partnerId != null)
				.map((statistic) => ({
					...statistic,
					actions: [],
				})) ?? [],
		[statistics],
	);

	const columns = [
		columnHelper.accessor((row) => row.partnerId, {
			id: "partnerId",
			header: t(partnerStatisticsResource.partnerName),
			maxSize: 30,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="body2">
					{row.original.partnerId != null ? (
						<Link to={PartnerPaths.Detail.path(row.original.partnerId)}>
							<Typography color="secondary">
								{row.original.partnerName ?? `${row.original.partnerName}`}
							</Typography>
						</Link>
					) : (
						(row.original.partnerName ?? "Unknown partner")
					)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.openClaims, {
			id: "openClaims",
			maxSize: 60,
			header: t(partnerStatisticsResource.openClaims),
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="body2">{row.original.openClaims}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.totalClaims, {
			id: "totalClaims",
			header: t(partnerStatisticsResource.totalClaims),
			maxSize: 60,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="body2">{row.original.totalClaims}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.ordersDone, {
			id: "ordersDone",
			header: t(partnerStatisticsResource.ordersDone),
			maxSize: 60,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="body2">{row.original.ordersDone}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.ordersConfirmed, {
			id: "ordersConfirmed",
			header: t(partnerStatisticsResource.ordersConfirmed),
			maxSize: 60,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="body2">{row.original.ordersConfirmed}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.ordersInProgress, {
			id: "ordersInProgress",
			header: t(partnerStatisticsResource.ordersInProgress),
			maxSize: 60,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="body2">{row.original.ordersInProgress}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.ordersCancelled, {
			id: "ordersCancelled",
			header: t(partnerStatisticsResource.ordersCancelled),
			maxSize: 60,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="body2">{row.original.ordersCancelled}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.ordersCancelled, {
			id: "ordersCancelledPercentage",
			header: t(partnerStatisticsResource.ordersCancelledPercentage),
			maxSize: 150,
			enableColumnActions: false,
			enableSorting: true,
			sortingFn: (rowA, rowB) => {
				const a = calcCancelledOrdersPercentage(
					rowA.original.ordersDone,
					rowA.original.ordersCancelled,
				);
				const b = calcCancelledOrdersPercentage(
					rowB.original.ordersDone,
					rowB.original.ordersCancelled,
				);
				return a - b;
			},
			Cell: ({ row }) => {
				const percentage = calcCancelledOrdersPercentage(
					row.original.ordersDone,
					row.original.ordersCancelled,
				);
				const allocatedJobs =
					row.original.ordersDone + row.original.ordersCancelled;
				return (
					<>
						<Typography variant="body2">{percentage.toString()}%</Typography>
						<Typography variant="caption" color="textSecondary">
							({allocatedJobs.toString()} allocated jobs)
						</Typography>
					</>
				);
			},
		}),
	];

	const tableOptions = {
		...defaultTableConfig,
		columns,
		initialState: {
			pagination: {
				pageSize: 100,
				pageIndex: 0,
			},
		},
		data: tableData,
		state: {
			showSkeletons: isLoading,
			isLoading,
		},
	};

	return tableOptions;
};
